import { useEffect, useMemo, useState } from "react"
import { Globe, CheckCircle2, Clock3, Send, Sparkles, Lock, Rocket, Copy, ExternalLink } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import StorefrontLiveQrTools from "../../components/Storefront/StorefrontLiveQrTools"
import StorefrontStatsCard from "../../components/Storefront/StorefrontStatsCard"
import StorefrontPublicOrderCard from "../../components/Storefront/StorefrontPublicOrderCard"
import { getCurrentPlan, hasFeature } from "../../utils/permissions"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"
import { getLatestEcommerceRequest, submitEcommerceRequest } from "../../api/auth/ecommerce"
import { buildStorefrontShortUrl, buildStorefrontUrl } from "../../utils/storefront"
import { isCarStore } from "../../utils/storeCategory"

type RequestStatus =
  | "PENDING_REVIEW"
  | "IN_REVIEW"
  | "NEEDS_INFO"
  | "IN_PROGRESS"
  | "LIVE"

type EcommerceRequestPayload = {
  _id?: string
  storeId?: string
  status: RequestStatus
  submittedAt: string
  contactName: string
  contactPhone: string
  preferredDomain: string
  businessType: string
  catalogSize: string
  requiredPages: string
  notes: string
}

const statusLabel: Record<RequestStatus, string> = {
  PENDING_REVIEW: "En attente de revue",
  IN_REVIEW: "En revue",
  NEEDS_INFO: "Informations requises",
  IN_PROGRESS: "En cours de réalisation",
  LIVE: "En ligne",
}

export default function EcommerceRequest() {
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const dispatch = useDispatch()
  const user = userdata?.user
  const store = userdata?.store
  const currentPlan = getCurrentPlan(userdata)
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"
  const canUseEcommerce = hasFeature(currentPlan, "ECOMMERCE")

  const [openSubscription, setOpenSubscription] = useState(false)
  const [status, setStatus] = useState<RequestStatus>("PENDING_REVIEW")
  const [contactName, setContactName] = useState(user?.fullName || "")
  const [contactPhone, setContactPhone] = useState(user?.phone || "")
  const [preferredDomain, setPreferredDomain] = useState("")
  const [businessType, setBusinessType] = useState("")
  const [catalogSize, setCatalogSize] = useState("")
  const [requiredPages, setRequiredPages] = useState("")
  const [notes, setNotes] = useState("")
  const [submittedAt, setSubmittedAt] = useState("")
  const [success, setSuccess] = useState("")
  const [error, setError] = useState("")
  const [loadingLatest, setLoadingLatest] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedDirect, setCopiedDirect] = useState(false)
  const storefrontShortUrl = useMemo(() => buildStorefrontShortUrl(store), [store])
  const storefrontUrl = useMemo(() => buildStorefrontUrl(store), [store])
  const carStore = isCarStore(store)

  useEffect(() => {
    if (!user) return
    setContactName((prev: string) => prev || user?.fullName || "")
    setContactPhone((prev: string) => prev || user?.phone || "")
  }, [user])

  useEffect(() => {
    if (!store?._id || !canUseEcommerce) return

    const loadLatest = async () => {
      setLoadingLatest(true)
      try {
        const response = await getLatestEcommerceRequest(store._id)
        const latest: EcommerceRequestPayload | null = response?.data || null
        if (!latest) return

        setStatus((latest.status as RequestStatus) || "PENDING_REVIEW")
        setSubmittedAt(latest.submittedAt || "")
        setContactName(latest.contactName || user?.fullName || "")
        setContactPhone(latest.contactPhone || user?.phone || "")
        setPreferredDomain(latest.preferredDomain || "")
        setBusinessType(latest.businessType || "")
        setCatalogSize(latest.catalogSize || "")
        setRequiredPages(latest.requiredPages || "")
        setNotes(latest.notes || "")
      } catch (e: any) {
        console.error(e)
      } finally {
        setLoadingLatest(false)
      }
    }

    loadLatest()
  }, [store?._id, canUseEcommerce, user?.fullName, user?.phone])

  const submit = async () => {
    setError("")
    setSuccess("")

    if (!contactName.trim() || !contactPhone.trim() || !businessType.trim()) {
      setError("Veuillez remplir les champs obligatoires.")
      return
    }

    if (!store?._id) {
      setError("Boutique introuvable. Veuillez recharger la page.")
      return
    }

    const payload: EcommerceRequestPayload = {
      status: "PENDING_REVIEW",
      submittedAt: new Date().toISOString(),
      contactName: contactName.trim(),
      contactPhone: contactPhone.trim(),
      preferredDomain: preferredDomain.trim(),
      businessType: businessType.trim(),
      catalogSize: catalogSize.trim(),
      requiredPages: requiredPages.trim(),
      notes: notes.trim(),
    }

    setSubmitting(true)
    try {
      const response = await submitEcommerceRequest(store._id, payload)
      const created: EcommerceRequestPayload | null = response?.data || null

      setStatus((created?.status as RequestStatus) || "PENDING_REVIEW")
      setSubmittedAt(created?.submittedAt || payload.submittedAt)
      setSuccess(response?.message || "Demande envoyée. Nous vous contacterons pour la suite.")
    } catch (e: any) {
      console.error(e)
      setError(e?.message || "Impossible d'envoyer la demande pour le moment.")
    } finally {
      setSubmitting(false)
    }
  }

  const copyValue = async (
    value: string,
    onCopied: (value: boolean) => void
  ) => {
    if (!value || typeof navigator === "undefined" || !navigator.clipboard) return

    try {
      await navigator.clipboard.writeText(value)
      onCopied(true)
      window.setTimeout(() => onCopied(false), 2000)
    } catch (e) {
      console.error(e)
    }
  }

  if (!canUseEcommerce) {
    return (
      <>
        <div className="max-w-4xl space-y-6">
          <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-700 to-blue-900 p-6 sm:p-7 text-white shadow-xl">
            <div className="pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full bg-cyan-300/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

            <div className="relative flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center">
                <Lock size={20} />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Module E-commerce</h1>
                <p className="mt-2 text-sm text-white/85 max-w-2xl">
                  Cette fonctionnalité est disponible uniquement avec le plan BUSINESS.
                </p>
              </div>
            </div>
          </section>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Passez au plan BUSINESS pour soumettre vos besoins web, suivre l'avancement et lancer votre boutique en ligne.
          </div>

          <button
            onClick={() => setOpenSubscription(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-white hover:bg-blue-700 transition"
          >
            <Rocket size={16} />
            Passer au plan BUSINESS
          </button>
        </div>

        <SubscriptionModal
          isOpen={openSubscription}
          currentPlan={currentPlan}
          currentBillingCycle={currentBillingCycle}
          plans={availablePlans}
          onClose={() => setOpenSubscription(false)}
          onUpgrade={(planKey, billingCycle) =>
            runSubscriptionUpgrade({
              planKey,
              billingCycle,
              currentPlan,
              currentBillingCycle,
              user,
              dispatch,
              onClose: () => setOpenSubscription(false),
            })
          }
        />
      </>
    )
  }

  return (
    <div className="space-y-8 max-w-6xl">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-700 to-blue-900 p-6 sm:p-7 text-white shadow-xl">
        <div className="pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full bg-cyan-300/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

        <div className="relative flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center">
            <Globe size={20} />
          </div>
          <div>
            <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
              <Sparkles size={12} /> Ecommerce Studio
            </p>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold">Demande site e-commerce</h1>
            <p className="mt-2 text-sm text-white/85 max-w-2xl">
              Soumettez vos besoins, puis suivez le statut de réalisation de votre site.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8 bg-white rounded-3xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-slate-800">Formulaire de besoin</h2>
            <span className="text-xs rounded-full bg-blue-50 px-2.5 py-1 text-blue-700">Plan BUSINESS</span>
          </div>

          {error && <div className="text-sm bg-red-50 border border-red-100 text-red-700 p-3 rounded-xl">{error}</div>}
          {success && <div className="text-sm bg-green-50 border border-green-100 text-green-700 p-3 rounded-xl">{success}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nom contact *">
              <input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </Field>
            <Field label="Téléphone contact *">
              <input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </Field>
          </div>

          <Field label="Domaine souhaité">
            <input
              value={preferredDomain}
              onChange={(e) => setPreferredDomain(e.target.value)}
              placeholder="ex: ma-boutique.com"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
            />
          </Field>

          <Field label="Type d'activité *">
            <input
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              placeholder="ex: Électronique, prêt-à-porter..."
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Volume catalogue">
              <input
                value={catalogSize}
                onChange={(e) => setCatalogSize(e.target.value)}
                placeholder="ex: 150 produits"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </Field>
            <Field label="Pages souhaitées">
              <input
                value={requiredPages}
                onChange={(e) => setRequiredPages(e.target.value)}
                placeholder="Accueil, Catalogue, Paiement..."
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </Field>
          </div>

          <Field label="Besoins détaillés">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              placeholder="Décrivez votre besoin, branding, intégrations, délais..."
            />
          </Field>

          <button
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-60"
          >
            <Send size={16} />
            {submitting ? "Envoi..." : "Envoyer la demande"}
          </button>
        </section>

        <aside className="lg:col-span-4 space-y-5">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <Globe size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-slate-800">
                  {carStore ? "URL publique de votre parc auto" : "URL publique de votre boutique"}
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  {carStore
                    ? "Ce lien pointera vers la page concessionnaire listant les vehicules actifs de votre parc auto."
                    : "Ce lien pointera vers la vitrine publique listant les produits actifs de la boutique."}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Lien généré</p>
              <p className="mt-2 break-all text-sm font-semibold text-slate-800">
                {storefrontShortUrl || "Lien indisponible"}
              </p>
              {storefrontUrl && storefrontShortUrl && storefrontUrl !== storefrontShortUrl ? (
                <p className="mt-2 break-all text-xs text-slate-500">Lien direct: {storefrontUrl}</p>
              ) : null}
              <p className="mt-2 text-xs text-slate-500">
                Identifiant utilisé: {store?.publicStoreSlug || store?.storeSlug || store?.slug || store?._id || "-"}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => copyValue(storefrontShortUrl, setCopied)}
                disabled={!storefrontShortUrl}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Copy size={16} />
                {copied ? "Copié" : "Copier le lien court"}
              </button>

              <a
                href={storefrontShortUrl || "#"}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition ${
                  storefrontShortUrl ? "bg-slate-900 hover:bg-slate-800" : "pointer-events-none bg-slate-300"
                }`}
              >
                <ExternalLink size={16} />
                {carStore ? "Ouvrir le parc auto" : "Ouvrir la vitrine"}
              </a>

              {storefrontUrl && storefrontUrl !== storefrontShortUrl ? (
                <button
                  type="button"
                  onClick={() => copyValue(storefrontUrl, setCopiedDirect)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <Copy size={16} />
                  {copiedDirect ? "Copié" : "Copier le lien direct"}
                </button>
              ) : null}
            </div>
          </div>

          <StorefrontLiveQrTools store={store} />
          <StorefrontPublicOrderCard
            store={store}
            defaultContactName={user?.fullName || user?.name || ""}
            defaultWhatsappNumber={user?.phone || ""}
          />
          <StorefrontStatsCard store={store} />

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold text-slate-800">Suivi du statut</h2>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Statut actuel</p>
              <p className="mt-1 font-semibold text-slate-800">
                {loadingLatest ? "Chargement..." : statusLabel[status]}
              </p>
            </div>

            {submittedAt ? (
              <p className="text-sm text-slate-600">
                Dernière soumission: <span className="font-medium">{new Date(submittedAt).toLocaleString()}</span>
              </p>
            ) : (
              <p className="text-sm text-slate-500">Aucune demande soumise pour le moment.</p>
            )}

            <div className="space-y-3 pt-2">
              <Step done={true} label="Demande reçue" />
              <Step done={status !== "PENDING_REVIEW"} label="Analyse du besoin" />
              <Step done={status === "IN_PROGRESS" || status === "LIVE"} label="Conception / développement" />
              <Step done={status === "LIVE"} label="Mise en ligne" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function Step({ done, label }: { done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {done ? <CheckCircle2 size={16} className="text-green-600" /> : <Clock3 size={16} className="text-slate-400" />}
      <span className={done ? "text-slate-700" : "text-slate-500"}>{label}</span>
    </div>
  )
}
